import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { prepareHeaders, API, buildError } from '../../api/apis';
import { ApiResponse, CreateUserRoleMappingRequest, CreateUserRoleMappingResponse, DeleteUserRoleMappingRequest, DeleteUserRoleMappingResponse, ListUserRoleMappingRequest, ListUserRoleMappingResponse, UpdateUserRoleMappingRequest, UpdateUserRoleMappingResponse } from '../../interfaces/interfaces';

export const userRolesService = createApi({
  reducerPath: 'userRolesApi',
  tagTypes: ['Users'],
  baseQuery: fetchBaseQuery({
    prepareHeaders: prepareHeaders,
  }),
  endpoints: (builder) => ({
    getUsers: builder.query({
      queryFn: async (request: ListUserRoleMappingRequest, { dispatch, getState }, extraOptions, baseQuery) => {
        // First, get the initial page
        const initialResult = await baseQuery(API.FETCH_USERS(request));

        if (initialResult.error) {
          return { error: buildError(initialResult.error, "") };
        }

        const initialResponse = initialResult.data as ApiResponse;
        const initialData = initialResponse.Response as ListUserRoleMappingResponse;

        let allUsers: any[] = initialData.UserDetails || [];
        let nextToken = initialData.NextToken;
        let hasMore = !!nextToken;

        // Return initial data immediately
        const finalResult = {
          data: {
            ...initialData,
            UserDetails: allUsers,
          } as ListUserRoleMappingResponse
        };

        // If there's more data, start background fetching
        if (hasMore) {
          // Use setTimeout to allow the initial data to be cached first
          setTimeout(async () => {
            while (hasMore) {
              try {
                const currentRequest = { ...request, NextToken: nextToken };
                const result = await baseQuery(API.FETCH_USERS(currentRequest));

                if (result.error) {
                  break;
                }

                const response = result.data as ApiResponse;
                const userData = response.Response as ListUserRoleMappingResponse;

                if (userData.UserDetails) {
                  allUsers = [...allUsers, ...userData.UserDetails];

                  // Update cache with new data
                  dispatch(
                    userRolesService.util.updateQueryData('getUsers', request, (draft) => {
                      draft.UserDetails = allUsers;
                      draft.NextToken = userData.NextToken;
                    })
                  );
                }

                nextToken = userData.NextToken;
                hasMore = !!nextToken;

                // Add delay to see incremental updates
                if (hasMore) {
                  await new Promise(resolve => setTimeout(resolve, 500));
                }

              } catch (error) {
                console.error('Error fetching next page:', error);
                break;
              }
            }
          }, 0);
        }

        return finalResult;
      },
      providesTags: () => [{ type: 'Users', id: 'LIST' }],
    }),
    createUser: builder.mutation({
      query: (request: CreateUserRoleMappingRequest) => ({
        url: API.CREATE_USER(),
        method: 'POST',
        body: request
      }),
      transformResponse: (response: ApiResponse) => {
        return response.Response as CreateUserRoleMappingResponse;
      },
      transformErrorResponse: (response) => {
        return buildError(response, "");
      },
      async onQueryStarted(request, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;

          // Try to update existing cache entries
          const state = getState();
          const queries = state.userRolesApi.queries;

          // Look for any getUsers cache entries
          let updated = false;

          for (const [key, query] of Object.entries(queries)) {
            if (key.includes('getUsers') && query?.data) {
              try {
                dispatch(
                  userRolesService.util.updateQueryData('getUsers', query.originalArgs || {}, (draft) => {
                    if (draft && draft.UserDetails) {
                      data['UserStatus'] = "staged";
                      draft.UserDetails.push(data);
                      updated = true;
                    }
                  })
                );
                break; // Exit after first successful update
              } catch (e) {
                console.warn('Cache update failed for key:', key);
                continue;
              }
            }
          }

          // If no cache was updated, invalidate tags to trigger refetch
          if (!updated) {
            dispatch(userRolesService.util.invalidateTags([{ type: 'Users', id: 'LIST' }]));
          }

        } catch (error) {
          console.error('Create user failed:', error);
          // Invalidate on error to ensure consistency
          dispatch(userRolesService.util.invalidateTags([{ type: 'Users', id: 'LIST' }]));
        }
      },
    }),
    updateUser: builder.mutation({
      query: (request: UpdateUserRoleMappingRequest) => {
        const bodyRequest = {
          FirstName: request.FirstName,
          LastName: request.LastName,
          Permissions: request.Permissions,
          Preferences: request.Preferences,
        };

        return {
          url: API.UPDATE_USER(request),
          method: 'PUT',
          body: bodyRequest
        };
      },
      transformResponse: (response: ApiResponse) => {
        return response.Response as UpdateUserRoleMappingResponse;
      },
      transformErrorResponse: (response) => {
        return buildError(response, "");
      },
      async onQueryStarted(request, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;

          // Try to update existing cache entries
          const state = getState();
          const queries = state.userRolesApi.queries;

          // Look for any getUsers cache entries
          let updated = false;

          for (const [key, query] of Object.entries(queries)) {
            if (key.includes('getUsers') && query?.data) {
              try {
                dispatch(
                  userRolesService.util.updateQueryData('getUsers', query.originalArgs || {}, (draft) => {
                    if (draft && draft.UserDetails) {
                      const userIndex = draft.UserDetails.findIndex(user =>
                        user.UserName === request.UserName
                      );
                      if (userIndex !== -1) {
                        // Update the existing user with the response data
                        draft.UserDetails[userIndex] = { ...draft.UserDetails[userIndex], ...data };
                        updated = true;
                      }
                    }
                  })
                );
                break; // Exit after first successful update
              } catch (e) {
                console.warn('Cache update failed for key:', key);
                continue;
              }
            }
          }

          // If no cache was updated, invalidate tags to trigger refetch
          if (!updated) {
            dispatch(userRolesService.util.invalidateTags([{ type: 'Users', id: 'LIST' }]));
          }

        } catch (error) {
          console.error('Update user failed:', error);
          // Invalidate on error to ensure consistency
          dispatch(userRolesService.util.invalidateTags([{ type: 'Users', id: 'LIST' }]));
        }
      },
    }),
    deleteUsers: builder.mutation({
      query: (request: DeleteUserRoleMappingRequest) => ({
        url: API.DELETE_USER(request),
        method: 'DELETE',
      }),
      transformResponse: (response: ApiResponse) => {
        return response.Response as DeleteUserRoleMappingResponse;
      },
      transformErrorResponse: (response) => {
        return buildError(response, "");
      },
      async onQueryStarted(request, { dispatch, queryFulfilled, getState }) {
        try {
          await queryFulfilled;

          // Try to update existing cache entries
          const state = getState();
          const queries = state.userRolesApi.queries;

          // Look for any getUsers cache entries
          let updated = false;

          for (const [key, query] of Object.entries(queries)) {
            if (key.includes('getUsers') && query?.data) {
              try {
                dispatch(
                  userRolesService.util.updateQueryData('getUsers', query.originalArgs || {}, (draft) => {
                    if (draft && draft.UserDetails) {
                      // Remove the deleted user
                      const initialLength = draft.UserDetails.length;
                      draft.UserDetails = draft.UserDetails.filter(user =>
                        user.UserName !== request.UserName
                      );
                      // Update count if user was actually removed
                      if (draft.UserDetails.length < initialLength) {
                        updated = true;
                      }
                    }
                  })
                );
                break; // Exit after first successful update
              } catch (e) {
                console.warn('Cache update failed for key:', key);
                continue;
              }
            }
          }

          // If no cache was updated, invalidate tags to trigger refetch
          if (!updated) {
            dispatch(userRolesService.util.invalidateTags([{ type: 'Users', id: 'LIST' }]));
          }

        } catch (error) {
          console.error('Delete user failed:', error);
          // Invalidate on error to ensure consistency
          dispatch(userRolesService.util.invalidateTags([{ type: 'Users', id: 'LIST' }]));
        }
      },
    }),
    getUserRolesAndPermissions: builder.query<any[], void>({
      queryFn: async (_, queryApi, extraOptions, baseQuery) => {
        const responses = await Promise.allSettled([
          baseQuery({ url: API.FETCH_PERMISSION_POLICIES(), method: 'GET' }),
          baseQuery({ url: API.FETCH_XACCOUNT_ROLES(), method: 'GET' })
        ]);

        const permissionResponse: any = responses[0];
        const rolesResponse: any = responses[1];

        const errors: string[] = [];
        const allowedRolesChoices: any[] = [];

        if (permissionResponse.status === 'rejected') {
          errors.push('Failed to fetch permission policies');
        } else if (permissionResponse.status === 'fulfilled') {
          const permissionsData = permissionResponse.value.data.Response;
          const allowedRoles = permissionsData?.Permissions || [];
          const accountIds = rolesResponse.status === 'fulfilled' ? rolesResponse.value.data.Response.AccountIds : [];

          allowedRoles.forEach((item: Record<string, any>) => {
            if (item.AccountIdParam === 'Mandatory') {
              accountIds.forEach((account: string) => {
                allowedRolesChoices.push({
                  value: `${account}/${item?.Policy}`,
                  text: `${account}/${item.Policy}`,
                });
              });
            } else {
              allowedRolesChoices.push({
                value: `${item?.Policy}`,
                text: item.Policy,
              });
            }
          });
        }

        if (rolesResponse.status === 'rejected') {
          errors.push('Failed to fetch account roles');
        }

        if (errors.length) {
          return {
            error: {
              status: 500,
              data: {
                messages: errors,
                allowedRolesChoices,
              },
            },
          };
        }

        return { data: allowedRolesChoices };
      },
    }),
  }),
});

export const { useGetUsersQuery, useCreateUserMutation, useUpdateUserMutation, useDeleteUsersMutation,
  useGetUserRolesAndPermissionsQuery } = userRolesService;
